import { ComponentType, LazyExoticComponent, lazy } from "react";

const RELOADED_AT_KEY = "lazyWithReload.reloadedAt";
// One reload per this window: a real outage then shows the error page instead
// of reloading forever.
const RELOAD_COOLDOWN_MS = 10_000;

function isChunkLoadError(error: unknown): boolean {
  const { name, message } = (error || {}) as { name?: string; message?: string };
  return (
    name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module/i.test(message || "")
  );
}

function claimReload(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOADED_AT_KEY) || 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
    return true;
  } catch {
    // No storage (e.g. blocked): no way to stop a loop, so don't reload.
    return false;
  }
}

/**
 * React.lazy for route-level code that reloads the page once when its chunk
 * can't be fetched. After a deploy an open tab still asks for the old chunk
 * file names, which no longer exist; a reload picks up the new build. Any
 * other error, or a second failure within the cooldown, is rethrown to the
 * route's error page.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((error) => {
      if (isChunkLoadError(error) && claimReload()) {
        window.location.reload();
        return new Promise<{ default: T }>(() => undefined);
      }
      throw error;
    })
  );
}
